import { Observable, Subject as RxjsSubject } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ApplicationError } from "../../_errors/application-error";
import { SubjectModelListItem } from "../../_objects/subject-model";
import { ReportTemplate, ReportTemplateListItem } from "../../_offline/models/report-preview/report-template";
import { UtilsService } from "../../_services/_utils.service";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { LoginUserOnlineService } from "../../_services/http-api/login-user-online.service";
import { ReportTemplateService } from "../../_services/http-api/report-template.service";
import { SubjectModelService } from "../../_services/http-api/subject-model.service";
import { LangUpdateService } from "../../_services/lang-update.service";
import { SpinnerService } from "../../_services/spinner.service";
import { CommonErrorMessageDialogComponent } from "../../common-dialog/common-error-message-dialog.component";
import { ReportTemplateDeleteConfirmDialogComponent } from "./report-template-delete-confirm-dialog/report-template-delete-confirm-dialog.component";

export class SettingsReportTemplateList {
  id: string;
  name: string;
  isValid: boolean;
  lastUpdateDate: string;
  subjectModelName: string[];
  export: boolean;
}

@Component({
  selector: "app-dialog-report-template-import-confirm",
  template: `
    <h2 mat-dialog-title>{{ "settingsReportTemplateImportConfirmDialog.confirmation" | translate }}</h2>
    <div class="container" fxLayout="column" fxLayoutAlign="center center">
      <ng-container *ngIf="data.reportTemplate">
        <p>{{ "settingsReportTemplateImportConfirmDialog.reportTemplateName" | translate }}</p>
        <b>{{ data.reportTemplate.name }}</b>
      </ng-container>
      <p>{{ "settingsReportTemplateImportConfirmDialog.message" | translate }}</p>
      <mat-list>
        <mat-list-item>
          <p>{{ data.filename }}</p>
        </mat-list-item>
      </mat-list>
    </div>
    <div class="container" fxLayout="row" fxLayoutAlign="center center" position="fixed">
      <mat-dialog-actions>
        <button mat-button color="primary" mat-dialog-close>{{ "settingsReportTemplateImportConfirmDialog.no" | translate }}</button>
        <button mat-raised-button color="primary" (click)="confirm()">{{ "settingsReportTemplateImportConfirmDialog.yes" | translate }}</button>
      </mat-dialog-actions>
    </div>
  `,
  standalone: false
})
export class SettingsReportTemplateImportConfirmDialogComponent {
  constructor(
    private dialogRef: MatDialogRef<SettingsReportTemplateImportConfirmDialogComponent, { result: boolean }>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  confirm(): void {
    this.dialogRef.close({ result: true });
  }
}

@Component({
  selector: "app-settings-report-template",
  templateUrl: "./settings-report-template.component.html",
  styleUrls: ["./settings-report-template.component.scss"],
  standalone: false
})
export class SettingsReportTemplateComponent implements OnInit {
  title = "Report Template Settings";
  browserName: string;

  isAdmin = false;

  reportTemplatesListDataSource = new MatTableDataSource<SettingsReportTemplateList>();
  displayedColumns = ["name", "date", "subjectModelName", "import", "export", "delete"];

  reportTemplates: ReportTemplateListItem[];

  modelListDataSource = new MatTableDataSource<SubjectModelListItem>();
  model: SubjectModelListItem[];

  observableResponse: Observable<any> = new Observable<{}>();

  reportTemplateFlg: boolean;
  deleteReportTemplateFlg: boolean;

  isTemplateEditable: boolean;
  private onDestroy$ = new RxjsSubject();

  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private router: Router,
    private subjectModelService: SubjectModelService,
    private reportTemplateService: ReportTemplateService,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private loginUserService: LoginUserOnlineService,
    private snackBar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      this.title = this.translate.instant("settingsReportTemplate.title");
    });
  }

  ngOnInit() {
    try {
      this.isTemplateEditable = true;
      this.browserName = this.utilsService.getBrowserName();
      this.isAdmin = this.loginUserService.isAdminRole();
      this.oscSpinnerService.show();
      this.getReportTemplatesList();
    } catch (e) {
      this.oscSpinnerService.hide();
      console.log(e);
    }
  }

  getReportTemplatesList() {
    this.reportTemplateService.getReportTemplates(null, false).subscribe(
      (rt) => {
        this.reportTemplates = rt;
      },
      (error) => {
        this.reportTemplateFlg = false;
        this.oscSpinnerService.hide();
        console.log(error);
      },
      () => {
        this.subjectModelService.getSubjectModels(false).subscribe(
          (sb) => {
            this.model = sb;
          },
          (error) => {
            this.oscSpinnerService.hide();
            console.log(error);
          },
          () => {
            if (this.reportTemplates.length > 0) {
              this.loadReportTemplateDataSource();
            } else {
              this.oscSpinnerService.hide();
            }
          }
        );
      }
    );
  }

  getSubjectModelName(subjectModelIds: string[]): string[] {
    const result = [];
    for (const subjectModelId of subjectModelIds) {
      this.model.filter(function (item) {
        if (item.id === subjectModelId) {
          result.push(item.name);
        }
      });
    }
    return result;
  }

  loadReportTemplateDataSource() {
    const reportTemplatesList: SettingsReportTemplateList[] = [];

    if (this.reportTemplates) {
      for (const template of this.reportTemplates) {
        const tmpReportTemplatesList: SettingsReportTemplateList = new SettingsReportTemplateList();
        tmpReportTemplatesList.id = template.id;
        tmpReportTemplatesList.name = template.name;
        tmpReportTemplatesList.isValid = template.isValid;
        tmpReportTemplatesList.lastUpdateDate = template.lastUpdateDate;
        reportTemplatesList.push(tmpReportTemplatesList);
      }

      let subjectModelIds;
      for (const template of this.reportTemplates) {
        if (!template.id || template.id === null || template.id === undefined) {
          throw new ApplicationError("reportTemplates is not found.");
        }
        this.reportTemplateService.getReportTemplate(template.id).subscribe(
          (rt) => {
            subjectModelIds = rt.subjectModelIds;
          },
          (error) => {
            console.log(error);
          },
          () => {
            const index = reportTemplatesList.findIndex(({ id }) => id === template.id);
            reportTemplatesList[index].subjectModelName = this.getSubjectModelName(subjectModelIds);

            this.reportTemplatesListDataSource.data = reportTemplatesList;
            this.reportTemplateFlg = this.reportTemplatesListDataSource.data.length !== 0;
          }
        );
      }
    }
    this.oscSpinnerService.hide();
  }

  editReportTemplate(reportTemplate?: SettingsReportTemplateList) {
    let reportTemplateId: string;

    if (!this.isTemplateEditable) {
      return;
    }

    if (reportTemplate) {
      if (!reportTemplate.id) {
        throw new ApplicationError("ReportTemplate is invalid.");
      } else {
        reportTemplateId = reportTemplate.id;
      }
    } else {
      reportTemplateId = undefined;
    }

    this.gotoReportTemplateEdit(reportTemplateId);
  }

  gotoReportTemplateEdit(reportTemplateId?: string) {
    const childUrls = new Array<string>();
    childUrls.push("settings/report-template-design");

    if (reportTemplateId) {
      childUrls.push(reportTemplateId);
    }

    this.router.navigate(childUrls);
  }

  importReportTemplate(files: File[], reportTemplate: ReportTemplateListItem): void {
    if (!files || files.length !== 1) {
      throw new ApplicationError("Files are invalid.");
    }

    const file = files[0];

    this.openImportConfirmDialog(file.name, reportTemplate).subscribe((val) => {
      this.oscSpinnerService.show();
      if (val.result) {
        this.reportTemplateService.importReportTemplate(file, reportTemplate).subscribe(
          (value) => {},
          (error: HttpErrorResponse) => {
            this.oscSpinnerService.hide();
            if (error.status === 400) {
              this.openErrorMessageDialog(
                this.translate.instant("settingsReportTemplate.importReportTemplateFailed"),
                this.translate.instant("settingsReportTemplate.importReportTemplateFailedMessage")
              );
            } else {
              this.snackBar.open(this.translate.instant("settingsReportTemplate.importReportTemplateFailed"));
              console.log(error);
            }
          },
          () => {
            this.snackBar.open(this.translate.instant("settingsReportTemplate.importReportTemplateSuccessfully"));
            this.getReportTemplatesList();
          }
        );
      }
    });
  }

  /** DeleteダイアログでYesが押下されたらテンプレート削除
   * @param  {ReportTemplate} reportTemplate レポートテンプレート
   */
  openDeleteDialog(reportTemplate: ReportTemplate) {
    this.deleteReportTemplateFlg = true;
    this.openDeleteConfirmDialog(reportTemplate).subscribe((val) => {
      this.oscSpinnerService.show();
      if (val.result) {
        this.reportTemplateService.deleteReportTemplate(reportTemplate.id).subscribe(
          (value) => {},
          (error: HttpErrorResponse) => {
            this.oscSpinnerService.hide();
            if (error.status === 409) {
              this.openErrorMessageDialog(
                this.translate.instant("settingsReportTemplate.openDeleteDialogFailed"),
                this.translate.instant("settingsReportTemplate.openDeleteDialogFailedMessage")
              );
            } else {
              this.snackBar.open(this.translate.instant("settingsReportTemplate.openDeleteDialogFailed"));
              console.log(error);
            }
          },
          () => {
            this.snackBar.open(this.translate.instant("settingsReportTemplate.openDeleteDialogSuccessfully"));
            this.getReportTemplatesList();
          }
        );
      }
    });
  }

  exportReportTemplate(template: SettingsReportTemplateList) {
    this.oscSpinnerService.show();
    this.reportTemplateService.exportReportTemplate(template.id).subscribe(
      (blob) => {
        this.utilsService.download(blob, template.name + ".zip", null);
      },
      (error) => {
        this.oscSpinnerService.hide();
        this.snackBar.open(this.translate.instant("settingsReportTemplate.exportReportTemplateFailed"));
        console.log(error);
      },
      () => {
        this.snackBar.open(this.translate.instant("settingsReportTemplate.exportReportTemplateSuccessfully"));
        this.oscSpinnerService.hide();
      }
    );
  }

  openImportConfirmDialog(filename: string, reportTemplate: ReportTemplateListItem): Observable<{ result: boolean }> {
    const dialogRef = this.dialog.open(SettingsReportTemplateImportConfirmDialogComponent, {
      width: "550px",
      height: "400px",
      data: { reportTemplate: reportTemplate, filename: filename }
    });
    return dialogRef.afterClosed();
  }

  /** Deleteダイアログを表示する
   * @param  {ReportTemplate} reportTemplate レポートテンプレート
   * @returns Observable
   */
  openDeleteConfirmDialog(reportTemplate: ReportTemplate): Observable<{ result: boolean }> {
    const dialogRef = this.dialog.open(ReportTemplateDeleteConfirmDialogComponent, {
      width: "550px",
      height: "400px",
      data: { reportTemplate: reportTemplate }
    });
    return dialogRef.afterClosed();
  }

  isSafari(browserName): boolean {
    return this.utilsService.isSafari(browserName);
  }
  openErrorMessageDialog(firstMessage: string, secondMessage: string): void {
    this.dialog.open(CommonErrorMessageDialogComponent, {
      width: "450px",
      height: "360px",
      data: { firstMessage: firstMessage, secondMessage: secondMessage }
    });
  }
}
